'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { MessageProcessor } from '@a2ui/web_core/v0_9';
import type { A2uiClientAction, SurfaceModel } from '@a2ui/web_core/v0_9';
import { A2uiSurface } from '@a2ui/react/v0_9';
import type { ReactComponentImplementation } from '@a2ui/react/v0_9';
import {
  normalizeCatalogId,
  normalizeCardChildren,
  normalizeInputBindings,
  normalizeImageFit,
  dropDuplicateCreateSurface,
} from './normalize';
import { langbridgeCatalog } from './catalog';
import { a2uiThemeVars } from './theme-vars';
import './a2ui.css';

type Surface = SurfaceModel<ReactComponentImplementation>;

/** Inline sparkles glyph (lucide-shaped) — avoids a lucide-react dep in chat-ui. */
function SparkGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 3 1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3Z" />
    </svg>
  );
}

/**
 * A2UI(Google generative-UI) 표면 렌더러. 백엔드가 AG-UI CUSTOM("a2ui") 으로 흘려보낸
 * server_to_client 메시지 배열을 공식 렌더러(@a2ui/react · @a2ui/web_core)로 그린다.
 *
 * 보안: A2UI 는 선언적 데이터 포맷이며 임의 코드를 실행하지 않는다. langbridgeCatalog(basicCatalog
 * 스키마 + 일부 컴포넌트 DS 렌더러)의 사전 승인된 컴포넌트(Text/Button/Card/Column/Row/List/Image
 * 등)만 렌더되고, 카탈로그에 없는 컴포넌트 요청은 무시된다(= 신뢰 카탈로그 allowlist).
 *   - XSS: Text 는 @a2ui/react 가 markdown-it(기본 html:false + 링크 스킴 검증)로 렌더 → 기본 안전.
 *     이는 라이브러리 동작에 의존하므로 @a2ui/* 를 정확 버전으로 핀고정한다(package.json).
 *   - Image/링크의 외부 URL 은 제한하지 않는다. 신뢰 못 할 에이전트가 위협 모델이면 CSP img-src 검토.
 *
 * 사용자 상호작용(버튼 클릭 등)은 MessageProcessor 의 actionHandler 로 잡아 onAction 으로 올린다.
 */
export function A2uiPanel({
  messages,
  onAction,
  mode,
}: {
  messages: Record<string, unknown>[];
  onAction: (action: A2uiClientAction) => void;
  /** color-scheme 힌트 — basicCatalog 의 light-dark() 기본값 해석용. */
  mode?: 'light' | 'dark';
}) {
  // onAction 을 ref 로 잡아 프로세서를 재생성하지 않고도 최신 콜백을 호출한다.
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  const procRef = useRef<MessageProcessor<ReactComponentImplementation> | null>(null);
  const fedRef = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const [surfaces, setSurfaces] = useState<Array<[string, Surface]>>([]);

  // 액션 발생 시, sendDataModel 이 켜진 표면들의 현재 데이터모델(입력값)을 action.context 에 병합해 올린다.
  const handleAction = (a: A2uiClientAction) => {
    const cdm = procRef.current?.getClientDataModel?.();
    const surfaceData = (cdm as any)?.surfaces?.[a.surfaceId];
    const enriched =
      surfaceData && typeof surfaceData === 'object'
        ? { ...a, context: { ...(a.context ?? {}), ...surfaceData } }
        : a;
    onActionRef.current(enriched);
  };

  const makeProcessor = () =>
    new MessageProcessor<ReactComponentImplementation>([langbridgeCatalog], handleAction);

  useEffect(() => {
    // 프로세서는 렌더가 아니라 effect 에서 lazy 생성(StrictMode 이중 렌더로 인한 고아 인스턴스 방지).
    // 새 프로세서를 만들면 fedRef 를 반드시 0 으로 리셋한다 — StrictMode 언마운트 cleanup 이
    // 프로세서를 dispose 하고 procRef 를 비운 뒤 재마운트하면, fedRef(ref)는 살아남아 있어
    // messages.slice(fedRef) 가 빈 배열이 되어 서페이스가 한 번도 안 먹히는 버그를 막는다.
    if (!procRef.current) {
      procRef.current = makeProcessor();
      fedRef.current = 0;
    } else if (messages.length < fedRef.current) {
      // 누적 메시지가 줄면(= 리셋/새 세션) 프로세서를 새로 만든다(표면 초기화).
      procRef.current.model.dispose();
      procRef.current = makeProcessor();
      fedRef.current = 0;
    }
    const proc = procRef.current;
    const fresh = messages.slice(fedRef.current);
    if (fresh.length) {
      const existingSurfaceIds = new Set(proc.model.surfacesMap.keys());
      const normalized = dropDuplicateCreateSurface(
        normalizeInputBindings(
          normalizeImageFit(normalizeCardChildren(normalizeCatalogId(fresh, langbridgeCatalog.id))),
        ),
        existingSurfaceIds,
      );
      proc.processMessages(normalized as any);
      fedRef.current = messages.length;
    }
    setSurfaces([...proc.model.surfacesMap.entries()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // 언마운트 시 프로세서의 reactive 구독을 정리(누수 방지).
  useEffect(
    () => () => {
      procRef.current?.model.dispose();
      procRef.current = null;
    },
    [],
  );

  if (!surfaces.length) return null;

  return (
    <div
      ref={panelRef}
      data-testid="a2ui-panel"
      role="group"
      aria-label="생성형 UI"
      style={{ colorScheme: mode ?? 'light', ...a2uiThemeVars() } as CSSProperties}
    >
      {/* 헤더 — 이 표면이 에이전트가 생성한 UI임을 알리는 명확한 경계/식별 행(HIL 카드와 동일 패턴). */}
      <div className="a2ui-surface-header">
        <span className="a2ui-surface-badge" aria-hidden>
          <SparkGlyph />
        </span>
        <span className="a2ui-surface-title">생성형 UI</span>
      </div>
      <div className="a2ui-surface-body">
        {surfaces.map(([id, surface]) => (
          <A2uiSurface key={id} surface={surface} />
        ))}
      </div>
    </div>
  );
}
